import { BigNumber } from 'bignumber.js';
import { formatUnits, parseUnits } from 'viem';

import { USD_DECIMALS } from '@infinex/asset-config';

/**
 * Converts a decimal string amount to its on-chain representation with the correct number of decimals
 * @example
 * parseTokenAmount('1.23', 6) // '1230000'
 * parseTokenAmount('0.000123', 18) // '123000000000000'
 */
export function parseTokenAmount(amount: string, decimals: number): string {
  return parseUnits(amount, decimals).toString();
}

type TokenAmount = {
  amount: string | bigint;
  decimals: number;
};

/**
 * Formats a token amount from its on-chain representation to a human-readable number
 * @example
 * formatTokenAmount('1230000', 6) // 1.23
 * formatTokenAmount({ amount: '1230000', decimals: 6 }) // 1.23
 */
export function formatTokenAmount(
  amount: string | bigint,
  decimals: number
): number;
export function formatTokenAmount(params: TokenAmount): number;
export function formatTokenAmount(
  amountOrParams: string | bigint | TokenAmount,
  decimals?: number
): number {
  // If passed as object params
  if (typeof amountOrParams === 'object' && 'amount' in amountOrParams) {
    const { amount, decimals } = amountOrParams;
    return Number(formatUnits(BigInt(amount), decimals));
  }

  // If passed as separate parameters
  if (decimals === undefined) {
    throw new Error('Decimals must be provided when passing amount directly');
  }

  return Number(formatUnits(BigInt(amountOrParams), decimals));
}

export function formatTokenAmountUsd(amount: string | bigint): number {
  return formatTokenAmount(amount, USD_DECIMALS);
}

export function formatTokenAmountUsdForDisplay(
  amount: string | bigint
): string {
  const value = formatTokenAmount(amount, USD_DECIMALS);
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Calculates the recommended number of decimal places to display for an asset, based on its USD rate.
 *
 * This function aims to ensure that we:
 *  1. Show enough decimals so that small values (less than \$0.01) are still shown with reasonable precision.
 *  2. Hide extra decimals for larger values that would not be meaningful.
 *
 * @param rate - The USD rate of the asset. Must be a positive number. I.e. 1
 * ETH = $3000 where rate = 3000.
 * @returns The recommended number of decimal places to display.
 */
export function calculateAssetDecimalPrecisionForUSD(rate: number): number {
  if (rate <= 0) {
    console.error('Rate must be a positive number.');
    // NOTE: This is a hotfix to prevent errors when the rate is 0.
    // The rate is NEVER actually zero (as that would mean the asset is worthless)
    // we are just deriving a rate based on the balance and USD value of holdings, which is wrong
    // We should only ever pass the provided asset rate value to this function
    // TODO: Remove this once we have a proper fix.
    return Math.max(Math.ceil(Math.log10(0.000000000001) + 2), 0);
  }

  // For rates very close to 1 (eg. USDE), return exactly 2 decimal places
  if (Math.abs(rate - 1) < 0.001) {
    return 2;
  }

  // We add 2 to shift the "pos" by two decimal places (relevant decimals for
  // USD). We use Math.ceil to ensure we round up to the nearest integer.
  return Math.max(Math.ceil(Math.log10(rate) + 2), 0);
}

/**
 * Calculates the optimal number of decimal places to display for an asset based on its amounts.
 * This is useful when we want to show enough decimals to be precise, but hide any additional
 * decimals that would represent a value less than $0.01 USD.
 *
 * @param params.usdAmount - The USD amount as a string or bigint (with USD_DECIMALS precision). Optional if rateUsd is provided.
 * @param params.tokenAmount - The token amount as a string or bigint
 * @param params.tokenDecimals - The number of decimals for the token
 * @param params.rateUsd - The USD rate of the asset (optional, used if usdAmount is not provided)
 * @returns The recommended number of decimal places to display
 *
 * @example
 * // With USD amount
 * calculateAssetDecimalPrecision({
 *   usdAmount: '1000000', // $1.00 (6 decimals)
 *   tokenAmount: '1000000000000000000', // 1.0 (18 decimals)
 *   tokenDecimals: 18
 * })
 *
 * // With rate
 * calculateAssetDecimalPrecision({
 *   tokenAmount: '1000000000000000000', // 1.0 (18 decimals)
 *   tokenDecimals: 18,
 *   rateUsd: 3000 // $3000 per token
 * })
 */
export function calculateAssetDecimalPrecision({
  tokenAmount,
  tokenDecimals,
  usdAmount,
  rateUsd,
}: {
  tokenAmount: string | bigint;
  tokenDecimals: number;
  usdAmount?: string | bigint;
  rateUsd?: number | null;
}): number {
  try {
    if (tokenAmount === '0') {
      return 2;
    }

    // If we have the USD amount, use that for precision calculation
    if (usdAmount) {
      const rate = BigNumber(formatTokenAmountUsd(usdAmount)).div(
        formatTokenAmount(tokenAmount, tokenDecimals)
      );
      return calculateAssetDecimalPrecisionForUSD(rate.toNumber());
    }

    // If we have the rate, use that directly
    if (rateUsd !== undefined && rateUsd !== null) {
      return calculateAssetDecimalPrecisionForUSD(rateUsd);
    }

    throw new Error('Either usdAmount or rateUsd must be provided');
  } catch (e) {
    console.error('Error calculating asset decimal precision', {
      tokenAmount,
      tokenDecimals,
      usdAmount,
      rateUsd,
      error: e,
    });
    return 6;
  }
}

// TODO: Remove this function in asset amounts refactor
// This is a temporary function to get the decimal precision for assets with already formatted USD amounts
export function calculateFormattedAssetDecimalPrecision({
  usdAmount,
  tokenAmount,
  rateUsd,
}: {
  usdAmount?: string | bigint;
  tokenAmount: string | bigint;
  rateUsd?: number;
}) {
  return calculateAssetDecimalPrecision({
    usdAmount,
    tokenAmount,
    tokenDecimals: 1,
    rateUsd,
  });
}

/**
 * Formats a number with appropriate suffixes (B, M, K) for better readability
 * Returns the formatted value and suffix
 *
 * @example
 * formatAmountWithSuffix(1234) // { value: 1.234, suffix: 'K' }
 * formatAmountWithSuffix(12345678) // { value: 12.345678, suffix: 'M' }
 * formatAmountWithSuffix(1234567890) // { value: 1.23456789, suffix: 'B' }
 */
export function formatAmountWithSuffix(value: number): {
  value: number;
  suffix: string;
} {
  if (value >= 1_000_000_000_000) {
    return { value: value / 1_000_000_000_000, suffix: 'T' };
  }
  if (value >= 1_000_000_000) {
    return { value: value / 1_000_000_000, suffix: 'B' };
  }
  if (value >= 1_000_000) {
    return { value: value / 1_000_000, suffix: 'M' };
  }
  if (value >= 1_000) {
    return { value: value / 1_000, suffix: 'K' };
  }
  return { value, suffix: '' };
}

/**
 * Formats a number with appropriate suffixes (M, K) with round-down behavior
 * Returns the formatted value and suffix
 *
 * @example
 * formatAmountWithSuffixRoundDown(1234) // { value: 1, suffix: 'K' }
 * formatAmountWithSuffixRoundDown(1234567) // { value: 1.2, suffix: 'M' }
 * formatAmountWithSuffixRoundDown(12345678) // { value: 12, suffix: 'M' }
 */
export function formatAmountWithSuffixRoundDown(v: number): {
  value: number;
  suffix: string;
} {
  const { value, suffix } = formatAmountWithSuffix(v);
  return { value: Math.floor(value), suffix };
}
